import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { isPurposeEnabledForVendor } from "@devowl-wp/cookie-consent-web-client";
import { extendCommonGroupsStylesheet } from "@devowl-wp/web-cookie-banner";
import { useBanner } from "../../../../contexts/banner.js";
import { useBannerStylesheet } from "../../../../hooks/banner/useBannerStylesheet.js";
import { useAppropriateSafeguards } from "../../../../hooks/common/useAppropriateSafeguards.js";
import { useDurationText } from "../../../../hooks/common/useDurationText.js";
import { Checkbox } from "../../../common/checkbox.js";
import { CookieProperty } from "../../../common/groups/cookieProperty.js";
import { CookiePropertyDataTransmission } from "../../../common/groups/cookiePropertyDataTransmission.js";
import { CookiePropertyListLessRelevant } from "../../../common/groups/cookiePropertyListLessRelevant.js";
import { CookiePropertyTechnicalDefinitionsTable } from "../../../common/groups/cookiePropertyTechnicalDefinitionsTable.js";
import { LinkToggle } from "../../../common/linkToggle.js";
function getTcfDataRetentionTimeForPurpose(dataRetention, purposeId, isSpecial) {
    return dataRetention ? dataRetention[isSpecial ? "specialPurposes" : "purposes"][`${purposeId}`] || dataRetention.stdRetention : undefined;
}
const BannerTcfVendor = (param)=>{
    let { id } = param;
    var _lazyLoadedDataForSecondView_tcf, _deviceStorageDisclosure_disclosures;
    const { Cookie } = useBannerStylesheet().extend(...extendCommonGroupsStylesheet);
    const [isOpen, setIsOpen] = useState(false);
    const banner = useBanner();
    const generateDurationText = useDurationText();
    const { designVersion, tcfFilterBy, lazyLoadedDataForSecondView, tcf: { gvl, model, original: { vendorConfigurations } } } = banner;
    const { vendors: { [id]: vendor }, purposes, specialPurposes, features, specialFeatures, dataCategories } = gvl;
    const { name, [tcfFilterBy === "consent" ? "purposes" : "legIntPurposes"]: usedPurposes, flexiblePurposes, specialPurposes: usedSpecialPurposes, features: usedFeatures, specialFeatures: usedSpecialFeatures, dataDeclaration, usesCookies, cookieMaxAgeSeconds, cookieRefresh, usesNonCookieAccess, dataRetention } = vendor;
    const { dataProcessingInCountries, dataProcessingInCountriesSpecialTreatments } = useMemo(()=>Object.values(vendorConfigurations).filter((param)=>{
            let { vendorId } = param;
            return vendorId === id;
        })[0], [
        id
    ]);
    const filteredPurposes = useMemo(()=>[
            ...usedPurposes,
            // Add flexible purposes which are not yet stored in used purposes (unique)
            ...flexiblePurposes.filter((flexiblePurposeId)=>usedPurposes.indexOf(flexiblePurposeId) === -1)
        ].filter((purposeId)=>isPurposeEnabledForVendor(model, purposeId, tcfFilterBy === "legInt", vendor)), [
        id,
        tcfFilterBy
    ]);
    const { group: { descriptionFontSize }, i18n: { tcf: { declarations: i18nTcfDeclarations, dataRetentionPeriod, ...i18nTcf }, safetyMechanisms: { adequacyDecision, bindingCorporateRules, standardContractualClauses }, other, ...i18n }, activeAction } = banner;
    const { deprecated: i18nDeprecated } = i18n;
    const { urls, additionalInformation, deviceStorageDisclosure } = (lazyLoadedDataForSecondView == null ? void 0 : (_lazyLoadedDataForSecondView_tcf = lazyLoadedDataForSecondView.tcf) == null ? void 0 : _lazyLoadedDataForSecondView_tcf.vendors[id]) || {
        urls: [],
        additionalInformation: {},
        deviceStorageDisclosure: {}
    };
    const provider = additionalInformation == null ? void 0 : additionalInformation.legalAddress;
    const internationalTransfers = !!(additionalInformation == null ? void 0 : additionalInformation.internationalTransfers);
    const transferMechanisms = (additionalInformation == null ? void 0 : additionalInformation.transferMechanisms) || [];
    const checkedList = model[tcfFilterBy === "consent" ? "vendorConsents" : "vendorLegitimateInterests"];
    const isDisabled = activeAction === "history";
    // Save checked in own state cause the model does not trigger a re-render
    const modelIsChecked = checkedList.has(id);
    const [isChecked, setIsChecked] = useState(modelIsChecked);
    // Update our checked state depending on the original value (e.g. switch of legInt | consent filter)
    useEffect(()=>{
        setIsChecked(modelIsChecked);
    }, [
        modelIsChecked
    ]);
    const handleCheckbox = useCallback((checked)=>{
        try {
            checkedList[checked ? "set" : "unset"](id);
            setIsChecked(checked);
        } catch (e) {
        // Silence is golden.
        }
    }, [
        id,
        checkedList,
        setIsChecked
    ]);
    const { dataProcessingInUnsafeCountries, appropriateSafeguards } = useAppropriateSafeguards({
        dataProcessingInCountries,
        specialTreatments: dataProcessingInCountriesSpecialTreatments,
        tcf: {
            internationalTransfers,
            transferMechanisms
        }
    });
    const { privacy, legIntClaim } = (urls == null ? void 0 : urls[0]) || {
        langId: "",
        privacy: "",
        legIntClaim: ""
    };
    const listPurposes = useCallback((purposeIds, isSpecial)=>{
        if (isSpecial === void 0) isSpecial = false;
        return purposeIds.map((declarationId)=>`${(isSpecial ? specialPurposes : purposes)[declarationId].name}${dataRetention ? ` (${dataRetentionPeriod}: ${generateDurationText(getTcfDataRetentionTimeForPurpose(dataRetention, declarationId, false), "d")})` : ""}`).join(", ");
    }, [
        purposes,
        specialPurposes,
        dataRetention
    ]);
    return /*#__PURE__*/ _jsxs(Cookie, {
        children: [
            /*#__PURE__*/ _jsx(Checkbox, {
                isChecked: isChecked,
                isDisabled: isDisabled,
                fontSize: descriptionFontSize,
                onToggle: handleCheckbox,
                after: /*#__PURE__*/ _jsx(LinkToggle, {
                    onToggle: setIsOpen,
                    showMore: i18n.showMore,
                    hideMore: i18n.hideMore,
                    bullets: true
                }),
                children: /*#__PURE__*/ _jsx("strong", {
                    children: name
                })
            }),
            isOpen && /*#__PURE__*/ _jsxs(Fragment, {
                children: [
                    !!provider && /*#__PURE__*/ _jsx(CookieProperty, {
                        label: i18n.provider,
                        value: provider.split(";").join(", ")
                    }),
                    /*#__PURE__*/ _jsx(CookieProperty, {
                        label: i18n.providerPrivacyPolicyUrl,
                        value: privacy
                    }),
                    tcfFilterBy === "legInt" && /*#__PURE__*/ _jsx(CookieProperty, {
                        label: i18nTcf.legIntClaim,
                        value: legIntClaim
                    }),
                    designVersion < 10 && dataProcessingInUnsafeCountries.length > 0 && /*#__PURE__*/ _jsx(CookieProperty, {
                        label: i18nDeprecated.dataProcessingInUnsafeCountries,
                        value: dataProcessingInUnsafeCountries.join(", ")
                    }),
                    designVersion < 10 && appropriateSafeguards.length > 0 && /*#__PURE__*/ _jsx(CookieProperty, {
                        label: i18nDeprecated.appropriateSafeguard,
                        value: appropriateSafeguards.join(", ")
                    }),
                    filteredPurposes.length > 0 && /*#__PURE__*/ _jsx(CookieProperty, {
                        label: i18nTcfDeclarations.purposes.title,
                        value: listPurposes(filteredPurposes)
                    }),
                    usedSpecialPurposes.length > 0 && /*#__PURE__*/ _jsx(CookieProperty, {
                        label: i18nTcfDeclarations.specialPurposes.title,
                        value: listPurposes(usedSpecialPurposes, true)
                    }),
                    usedFeatures.length > 0 && /*#__PURE__*/ _jsx(CookieProperty, {
                        label: i18nTcfDeclarations.features.title,
                        value: usedFeatures.map((declarationId)=>features[declarationId].name).join(", ")
                    }),
                    usedSpecialFeatures.length > 0 && /*#__PURE__*/ _jsx(CookieProperty, {
                        label: i18nTcfDeclarations.specialFeatures.title,
                        value: usedSpecialFeatures.map((declarationId)=>specialFeatures[declarationId].name).join(", ")
                    }),
                    (dataDeclaration == null ? void 0 : dataDeclaration.length) > 0 && /*#__PURE__*/ _jsx(CookieProperty, {
                        label: i18nTcfDeclarations.dataCategories.title,
                        value: dataDeclaration.map((declarationId)=>dataCategories[declarationId].name).join(", ")
                    }),
                    /*#__PURE__*/ _jsx(CookieProperty, {
                        label: i18n.usesCookies,
                        value: usesCookies,
                        printValueAs: "boolean"
                    }),
                    usesCookies && /*#__PURE__*/ _jsx(CookieProperty, {
                        label: i18n.duration,
                        value: cookieMaxAgeSeconds <= 0 ? "Session" : generateDurationText(cookieMaxAgeSeconds, "s")
                    }),
                    /*#__PURE__*/ _jsx(CookieProperty, {
                        label: i18n.cookieRefresh,
                        value: cookieRefresh,
                        printValueAs: "boolean"
                    }),
                    /*#__PURE__*/ _jsx(CookieProperty, {
                        label: i18n.usesNonCookieAccess,
                        value: usesNonCookieAccess,
                        printValueAs: "boolean"
                    }),
                    /*#__PURE__*/ _jsxs(CookiePropertyListLessRelevant, {
                        expandable: designVersion > 9,
                        labelModifications: {
                            [i18n.technicalCookieName]: i18n.technicalCookieDefinitions
                        },
                        children: [
                            designVersion > 9 && /*#__PURE__*/ _jsx(CookiePropertyDataTransmission, {
                                dataProcessingInCountries: dataProcessingInCountries,
                                dataProcessingInCountriesSpecialTreatments: dataProcessingInCountriesSpecialTreatments,
                                mechanisms: (usedMechanisms)=>{
                                    const result = [
                                        ...usedMechanisms
                                    ];
                                    // E.g. Google uses `Adequacy Decision` instead of `Adequacy decision`
                                    const lowerCased = transferMechanisms.map((s)=>s.toLowerCase());
                                    if (lowerCased.indexOf("adequacy decision") > -1) {
                                        result.push(adequacyDecision);
                                    }
                                    if (lowerCased.indexOf("sccs") > -1 && usedMechanisms.indexOf("B") === -1) {
                                        result.push(standardContractualClauses);
                                    }
                                    if (lowerCased.indexOf("bcrs") > -1 && usedMechanisms.indexOf("E") === -1) {
                                        result.push(bindingCorporateRules);
                                    }
                                    if (lowerCased.indexOf("other") > -1) {
                                        result.push(other);
                                    }
                                    return result;
                                }
                            }),
                            (deviceStorageDisclosure == null ? void 0 : (_deviceStorageDisclosure_disclosures = deviceStorageDisclosure.disclosures) == null ? void 0 : _deviceStorageDisclosure_disclosures.length) > 0 && /*#__PURE__*/ _jsx(CookiePropertyTechnicalDefinitionsTable, {
                                codeDynamics: {},
                                definitions: deviceStorageDisclosure.disclosures.map((param)=>{
                                    let { type, identifier, domain, domains, maxAgeSeconds, cookieRefresh, purposes: disclosurePurposes } = param;
                                    return {
                                        type: getTcfCookieTypeLocalization(type),
                                        name: identifier,
                                        host: domains ? domains.join(",") : domain || "n/a",
                                        isSessionDuration: maxAgeSeconds !== null && maxAgeSeconds <= 0,
                                        duration: maxAgeSeconds,
                                        durationUnit: "s",
                                        purpose: (disclosurePurposes == null ? void 0 : disclosurePurposes.length) ? disclosurePurposes.map((purposeId)=>{
                                            var _purposes_purposeId;
                                            return (_purposes_purposeId = purposes[purposeId]) == null ? void 0 : _purposes_purposeId.name;
                                        }).filter(Boolean).join(", ") : undefined,
                                        children: /*#__PURE__*/ _jsx(CookieProperty, {
                                            label: i18n.cookieRefresh,
                                            value: cookieRefresh,
                                            printValueAs: "boolean"
                                        })
                                    };
                                })
                            })
                        ]
                    })
                ]
            })
        ]
    });
};
function getTcfCookieTypeLocalization(type) {
    switch(type){
        case "cookie":
            return "HTTP Cookie";
        case "web":
            return "LocalStorage, Session Storage, IndexDB";
        case "app":
            return "App";
        default:
            return type;
    }
}
export { BannerTcfVendor, getTcfCookieTypeLocalization, getTcfDataRetentionTimeForPurpose };
